import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_SERVICES = ['Fashion Photography', 'Street Photography', 'Event Photography', 'Car Photography'];

const inputClass = "w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors";

const CustomField = ({ field, value, onChange }) => {
  const label = <label className="block text-xs uppercase tracking-widest mb-2 text-gray-500">{field.label}</label>;

  if (field.field_type === 'dropdown') {
    return (
      <div>
        {label}
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} className={`${inputClass} appearance-none`} data-testid={`custom-field-${field.id}`}>
          <option value="" disabled>Select {field.label.toLowerCase()}</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (field.field_type === 'textarea') {
    return (
      <div>
        {label}
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} rows={3} placeholder={`Enter ${field.label.toLowerCase()}`} className={`${inputClass} resize-none`} data-testid={`custom-field-${field.id}`} />
      </div>
    );
  }
  return (
    <div>
      {label}
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} required={field.required} placeholder={`Enter ${field.label.toLowerCase()}`} className={inputClass} data-testid={`custom-field-${field.id}`} />
    </div>
  );
};

const InlineEnquiryForm = ({ heading = 'Ready to Book?', subheading = "Get in touch and let's create something amazing together." }) => {
  const [form, setForm] = useState({ name: '', email: '', service: '', enquiry: '' });
  const [customValues, setCustomValues] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, fieldsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/form-fields`),
      ]);
      setServiceOptions(catRes.data.map((c) => `${c.name} Photography`));
      setCustomFields(fieldsRes.data);
    } catch {
      setServiceOptions(FALLBACK_SERVICES);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleCustomChange = (fieldId, label, value) => {
    setCustomValues({ ...customValues, [fieldId]: { label, value } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const customFieldsPayload = {};
    Object.values(customValues).forEach(({ label, value }) => {
      if (value) customFieldsPayload[label] = value;
    });
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        custom_fields: Object.keys(customFieldsPayload).length > 0 ? customFieldsPayload : null,
      });
      toast.success('Enquiry sent successfully!');
      setSent(true);
      setForm({ name: '', email: '', service: '', enquiry: '' });
      setCustomValues({});
    } catch {
      toast.error('Failed to send enquiry. Please try again.');
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-t border-white/10" data-testid="inline-enquiry-success">
        <motion.div className="max-w-2xl mx-auto text-center" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
          <Send className="w-10 h-10 mx-auto mb-6 text-white/60" />
          <h2 className="text-2xl md:text-3xl font-normal mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Enquiry Sent</h2>
          <p className="text-gray-400 text-sm mb-8">Thank you for reaching out. I'll be in touch shortly.</p>
          <button onClick={() => setSent(false)} className="bg-white text-black hover:bg-gray-200 rounded-none px-8 py-3 text-sm uppercase tracking-widest transition-all duration-300" data-testid="inline-send-another">Send Another</button>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-t border-white/10" data-testid="inline-enquiry-section">
      <motion.div className="max-w-2xl mx-auto" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
        <h2 className="text-3xl md:text-5xl font-normal tracking-tight mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>{heading}</h2>
        <p className="text-gray-400 text-sm text-center mb-10 tracking-wide">{subheading}</p>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="inline-enquiry-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2 text-gray-500">Name</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Your name" className={inputClass} data-testid="inline-name" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2 text-gray-500">Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" className={inputClass} data-testid="inline-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest mb-2 text-gray-500">Shoot Type</label>
            <select name="service" value={form.service} onChange={handleChange} required className={`${inputClass} appearance-none`} data-testid="inline-service">
              <option value="" disabled>Select a shoot type</option>
              {serviceOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          {customFields.map((field) => (
            <CustomField
              key={field.id}
              field={field}
              value={customValues[field.id]?.value || ''}
              onChange={(val) => handleCustomChange(field.id, field.label, val)}
            />
          ))}
          <div>
            <label className="block text-xs uppercase tracking-widest mb-2 text-gray-500">Message</label>
            <textarea name="enquiry" value={form.enquiry} onChange={handleChange} required rows={4} placeholder="Tell me about your project..." className={`${inputClass} resize-none`} data-testid="inline-message" />
          </div>
          <button type="submit" disabled={sending} className="w-full bg-white text-black hover:bg-gray-200 rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3" data-testid="inline-submit">
            <Send size={16} />{sending ? 'Sending...' : 'Send Enquiry'}
          </button>
        </form>
      </motion.div>
    </section>
  );
};

export default InlineEnquiryForm;
